// =============================================================================
// domain/bestOpps.ts — the "setups I saw" log, and the two places it lies.
// -----------------------------------------------------------------------------
// A Best Opp is a setup the trader NOTICED, whether or not they took it. It is a
// FREE-TIER feature (the upgrade card: "Best Opps and Review moved to Free"), so
// it is one of the first screens a prospect meets.
//
// ── 🚨 THE HEADING LIES, AND THE SCREENSHOT IS WHAT PROVED IT ──
//
// The old client's section reads "Best Opps of the Day (N)" and applies NO FILTER
// WHATSOEVER — not the global date range, not the account filter, not "the day".
// `state.bestOpps` is rendered whole, newest date first.
//
// Josh's 2026-08-14 screenshot shows entries dated 2026-05-18 and 2026-05-22
// sitting under a `This week` filter. ⚠ Reading the heading and scoping this to a
// day would hide almost everything, and would look entirely deliberate. The
// heading is kept verbatim for parity; the SCOPE is the whole collection.
//
// ── 🚨 THE SECOND LIE IS IN THE SCHEMA ──
//
// `best_opps.est_rr` is a NUMERIC column. The input that feeds it is FREE TEXT
// with a "3.5" placeholder and no validation, and the old client saves it as
// `Number(value)`. So "3.5" survives, and "3.5-4" / "3 to 4" / "2R" become NaN,
// which serialises to null — the trader's typing is discarded on save with no
// error and no trace. `estRRToDb` refuses instead; see its note.
// =============================================================================

/** How much of the reflection a card shows. Beta's `.slice(0, 160)`. */
export const OPP_REFLECTION_CHARS = 160;

export interface BestOppIdentity {
  /** ISO `YYYY-MM-DD`. The DB column is DATE and NOT NULL. */
  date: string;
  contract: string;
  /** `HH:MM:SS`, optional — the input is `type=time step=1`. */
  time: string;
  patternId: string;
  grade: string;
  taken: boolean;
}

export interface BestOppContext {
  /**
   * ⚠ ALL THREE ZONES ARE FREE TEXT, NOT NUMBERS AND NOT RANGES. Josh's real
   * stop zone is `7477-7475` — descending. Any numeric parsing, ordering rule or
   * min/max pair would reject the way he actually writes them.
   */
  entryZone: string;
  stopZone: string;
  targetZone: string;
  /** ⚠ Free text in the form, NUMERIC in the column — see `estRRToDb`. */
  estRR: string;
  estRiskDollar: number;
}

export interface BestOpp {
  id: string;
  identity: BestOppIdentity;
  context: BestOppContext;
  reflection: { reason: string; wouldDoDifferent: string };
  linkage: { tradeId: string };
  screenshots: readonly string[];
}

/** A new, empty opp — beta's defaults, field for field. */
export function emptyBestOpp(id: string, todayIso: string): BestOpp {
  return {
    id,
    identity: { date: todayIso, contract: 'ES', time: '', patternId: '', grade: '', taken: false },
    context: { entryZone: '', stopZone: '', targetZone: '', estRR: '', estRiskDollar: 0 },
    reflection: { reason: '', wouldDoDifferent: '' },
    linkage: { tradeId: '' },
    screenshots: [],
  };
}

/**
 * Newest first, by the opp's own date.
 *
 * ⚠ NO FILTERING — see the header. Sorting only, and on `identity.date` alone,
 * which is what beta compares (`localeCompare` on the ISO string). Time is NOT a
 * tiebreak there, so two opps on one date keep their relative input order.
 */
export function sortBestOpps(opps: readonly BestOpp[]): BestOpp[] {
  return opps.slice().sort((a, b) => (b.identity.date || '').localeCompare(a.identity.date || ''));
}

export interface BestOppCard {
  id: string;
  contract: string;
  /** ⚠ ISO, printed raw — the card does NOT use the form's MM/DD/YYYY. */
  dateLabel: string;
  /** `identity.time`, or '' — appended after the date only when set. */
  timeLabel: string;
  patternLabel: string;
  taken: boolean;
  grade: string;
  reflection: string;
  /** True when the reflection was cut, so the view can mark it. */
  truncated: boolean;
  hasReflection: boolean;
  estRR: string;
  estRiskDollar: number | null;
  screenshotCount: number;
}

/**
 * One card's worth of view-model.
 *
 * ⚠ THE TRUNCATION IS DONE ON THE RAW TEXT, DELIBERATELY UNLIKE BETA. Beta runs
 * `escapeHtml(reason).slice(0, 160)`, which slices the ESCAPED string — so a
 * `&`, `<` or quote landing near the boundary is cut mid-entity (`&amp;` → `&am`)
 * and renders as literal junk. React escapes on render, so slicing first is both
 * simpler and correct. The CHARACTER COUNT still matches beta for the ordinary
 * case of text with no entities in the last few characters.
 */
export function bestOppCard(
  o: BestOpp,
  patternName: string | null,
): BestOppCard {
  const reason = o.reflection.reason ?? '';
  return {
    id: o.id,
    contract: o.identity.contract || '?',
    dateLabel: o.identity.date || '',
    timeLabel: o.identity.time || '',
    patternLabel: patternName || '(no pattern)',
    taken: !!o.identity.taken,
    grade: o.identity.grade || '',
    reflection: reason.slice(0, OPP_REFLECTION_CHARS),
    truncated: reason.length > OPP_REFLECTION_CHARS,
    hasReflection: reason.length > 0,
    // ⚠ `String(estRR || '—')` in beta: 0, '' and '0' all fall to the dash. Kept,
    // because an Est R:R of zero is not a real plan — it is an unfilled field.
    estRR: o.context.estRR && Number(o.context.estRR) !== 0 ? String(o.context.estRR) : '—',
    // ⚠ Falsy → null → the view shows a dash. A $0 risk is likewise "not entered".
    estRiskDollar: o.context.estRiskDollar ? o.context.estRiskDollar : null,
    screenshotCount: o.screenshots.length,
  };
}

/**
 * 🚨 EST R:R, FREE TEXT IN, NUMERIC COLUMN OUT — AND THE OLD CLIENT LOSES IT.
 *
 * The field invites free text: its placeholder is "3.5", nothing validates it,
 * and a trader who plans a band rather than a point writes "3.5-4" or "2 to 3".
 * Beta then saves `Number("3.5-4")` → NaN → serialised as null. The row is
 * written, the modal closes, a success toast appears, and the typing is gone.
 *
 * ▶ So this returns a discriminated result rather than a number: empty is a
 * legitimate empty (Josh's own entry has it blank), a parseable value converts,
 * and anything else is REFUSED so the caller can say so before saving. Refusing
 * is the whole point — silently storing null is what beta does.
 *
 * ⚠ Empty maps to `null`, not 0. Beta's `Number('')` is 0, which writes a real
 * zero into a numeric column and makes "never filled in" indistinguishable from
 * "planned a zero R:R". Both display as a dash, so nothing on screen changes;
 * the stored value stops lying.
 */
export type EstRRResult =
  | { ok: true; value: number | null }
  | { ok: false; reason: string };

export function estRRToDb(raw: string): EstRRResult {
  const s = (raw ?? '').trim();
  if (!s) return { ok: true, value: null };
  const n = Number(s);
  if (Number.isFinite(n)) return { ok: true, value: n };
  return {
    ok: false,
    reason: `Est R:R must be a single number, like 3.5 — "${s}" cannot be stored, ` +
      `and saving it would silently discard what you typed.`,
  };
}
